import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IPageContainer, PageContainerParamId } from '~/types/page-container'
import type { ITemplateParams } from '~/types/template'

import PageContainerCreation from './Create'
import PageContainerDuplication from './Duplicate'
import PageContainerEdition from './Edit'
import DetailsHeader from './Form/DetailsHeader'
import TemplateNotFound from './TemplateNotFound'

import { useTemplateDetailsQuery } from '../../Templates/queries'

import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import { useAppsListQuery } from '~/queries/applications'
import { usePageContainerDetailsQuery } from '~/queries/page-containers'
import DynamicScreenHeader from '~/views/DynamicScreens/PageContainers/Details/DynamicScreenHeader'

import './styles.scss'

const PageContainerDetails = (): ReactElement => {
  const navigate = useNavigate()

  const { pageContainerId } = useParams<{ pageContainerId?: PageContainerParamId }>()

  const { state }: { state?: { pageContainer?: IPageContainer } } = useLocation()

  const [selectedApp, setSelectedApp] = useState<IAppSummary>()

  const [selectedTemplateParams, setSelectedTemplateParams] = useState<ITemplateParams>()

  const isCreation = pageContainerId === 'creation'

  const isDuplication = pageContainerId === 'duplication'

  const isEdition = !isCreation && !isDuplication

  const pageContainerUuid = isEdition ? pageContainerId : state?.pageContainer?.uuid

  const { data: pageContainer, isLoading: isPageContainerLoading } = usePageContainerDetailsQuery(pageContainerUuid)

  const { data: appsData, isLoading: isAppSummaryLoading } = useAppsListQuery({ owned: true })

  const pageContainerTemplateKey = isEdition
    ? pageContainer?.template_key
    : state?.pageContainer?.template_key || selectedTemplateParams?.key

  const pageContainerTemplateVersion = isEdition
    ? pageContainer?.template_version
    : state?.pageContainer?.template_version || selectedTemplateParams?.version

  const pageContainerDeviceType = isEdition
    ? pageContainer?.device_type
    : state?.pageContainer?.device_type || selectedTemplateParams?.deviceType

  const pageContainerIsUnity = isEdition
    ? pageContainer?.is_unity
    : state?.pageContainer?.is_unity || selectedTemplateParams?.isUnity

  const { data: templateData, isLoadingAndEnabled: isTemplateLoading } = useTemplateDetailsQuery(
    {
      deviceType: pageContainerDeviceType as string,
      isUnity: pageContainerIsUnity as boolean,
      key: pageContainerTemplateKey as string,
      version: pageContainerTemplateVersion as string,
    },
    {
      enabled:
        !!pageContainerTemplateKey &&
        !!pageContainerTemplateVersion &&
        !!pageContainerDeviceType &&
        typeof pageContainerIsUnity === 'boolean',
    }
  )

  const appSummary =
    pageContainer &&
    appsData?.data?.find(
      (app) => app.app_key === pageContainer.app_key && app.device_type === pageContainer.device_type
    )

  const handleTemplateClick = (params: ITemplateParams) => {
    setSelectedTemplateParams(params)
  }

  const handleAppRowClick = ({ app_key, device_type }: { app_key: string; device_type: string }) => {
    const app = appsData?.data?.find((app) => app_key === app.app_key && device_type === app.device_type)
    setSelectedApp(app)
  }

  useEffect(() => {
    if (isDuplication && !state?.pageContainer) {
      navigate('/dynamic-screens/page-containers')
    }
  }, [isDuplication, state, navigate])

  const isLoading = pageContainerUuid && (isPageContainerLoading || isAppSummaryLoading || isTemplateLoading)

  if (isLoading) {
    return (
      <Layout className="page-container-details-form">
        <MwMLoader category="item" />
      </Layout>
    )
  }

  return (
    <div className="page-container-details-view">
      <DynamicScreenHeader
        backNavigationUrl="/dynamic-screens/page-containers"
        id={pageContainerId}
        onAppRowClick={handleAppRowClick}
        onTemplateClick={handleTemplateClick}
        selectedApp={selectedApp || appSummary}
      />
      <Layout className="page-container-details-form">
        {isCreation && selectedApp && selectedTemplateParams && templateData ? (
          <PageContainerCreation
            selectedApp={selectedApp}
            selectedTemplateKey={selectedTemplateParams.key}
            template={templateData}
            templateVersion={selectedTemplateParams.version}
          />
        ) : appSummary && pageContainer && templateData ? (
          isDuplication ? (
            <PageContainerDuplication pageContainer={pageContainer} selectedApp={appSummary} template={templateData} />
          ) : (
            isEdition && (
              <PageContainerEdition pageContainer={pageContainer} selectedApp={appSummary} template={templateData} />
            )
          )
        ) : pageContainer && !templateData && appSummary ? (
          <>
            <DetailsHeader pageContainer={pageContainer} status={pageContainer.status} title={pageContainer.title} />
            <TemplateNotFound
              pageContainer={pageContainer}
              selectedApp={appSummary}
              templateKey={selectedTemplateParams?.key}
            />
          </>
        ) : null}
      </Layout>
    </div>
  )
}

export default PageContainerDetails
